import { useEffect, useState } from "react";
import API from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ArrowUpRight, ArrowDownRight, Crown, Repeat, Calendar, CheckCircle2 } from "lucide-react";

const TIERS = [
  {
    key: "one_time",
    label: "Monthly",
    icon: Calendar,
    blurb: "One 28-day order at a time. 1 vial. No auto-renew.",
    color: "slate",
  },
  {
    key: "solo_subscribe",
    label: "1-Month Subscribe",
    icon: Repeat,
    blurb: "Auto-reorders every 28 days. 1 vial · $4 off the standard Monthly price.",
    color: "sky",
  },
  {
    key: "royal_90",
    label: "3-Month Subscribe",
    icon: Crown,
    blurb: "Auto-reorders every 3 months (84 days). 3 vials shipped together · lowest per-month price + guaranteed overnight shipping.",
    color: "amber",
  },
];

function tierLabel(t) { return TIERS.find((x) => x.key === t)?.label || t; }

/**
 * ChangePlanModal — patient self-serve plan upgrade / downgrade.
 *
 * Flow:
 *  1. Patient picks a target tier.
 *  2. Confirm → POST /api/subscriptions/{id}/request-tier-change.
 *  3. Backend schedules cancel_at_period_end, records pending request,
 *     flags queued future rxs for prescriber review.
 *  4. Modal shows "Switching to <new tier> on <date>" plus an undo option.
 */
export default function ChangePlanModal({ open, onClose, sub, onChanged }) {
  const [currentTier, setCurrentTier] = useState("");
  const [target, setTarget] = useState("");
  const [pending, setPending] = useState(null);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!open || !sub) return;
    let alive = true;
    setLoading(true);
    Promise.all([
      // Infer current tier from the subscription metadata exposed in product_name
      // OR fall back to the pending-change record.
      API.get("/subscriptions/me/plan-change").catch(() => ({ data: { pending: null } })),
    ]).then(([pendRes]) => {
      if (!alive) return;
      setPending(pendRes?.data?.pending || null);
      // Try to infer current tier: backend doesn't expose it directly on /me, so
      // fall back to "subscription" as a sane default; the pending record knows
      // the precise from_tier if there is one.
      setCurrentTier(pendRes?.data?.pending?.from_tier || "solo_subscribe");
      setTarget("");
    }).finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [open, sub?.id]);

  const submit = async () => {
    if (!target || target === currentTier) {
      toast.error("Pick a different plan");
      return;
    }
    setBusy(true);
    try {
      const { data } = await API.post(`/subscriptions/${sub.id}/request-tier-change`, { target_tier: target });
      toast.success(`Switching to ${tierLabel(data.to_tier)} on ${data.effective_at?.slice(0, 10) || "next renewal"}`);
      onChanged?.(data);
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Plan change failed");
    } finally {
      setBusy(false);
    }
  };

  const undo = async () => {
    setBusy(true);
    try {
      await API.post("/subscriptions/me/plan-change/cancel");
      toast.success("Plan change canceled — your current plan stays active");
      onChanged?.();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Undo failed");
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;
  const isDowngrade = (a, b) => (
    ["one_time", "solo_subscribe", "royal_90"].indexOf(b) <
    ["one_time", "solo_subscribe", "royal_90"].indexOf(a)
  );
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent className="max-w-lg" data-testid="change-plan-modal">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "'Outfit', sans-serif" }}>Change my plan</DialogTitle>
        </DialogHeader>
        {loading ? (
          <p className="py-6 text-sm text-slate-500">Loading…</p>
        ) : pending ? (
          <div className="space-y-4 py-2">
            <div className="rounded-xl bg-amber-50 border border-amber-200 p-4" data-testid="pending-plan-change-banner">
              <p className="text-sm font-semibold text-amber-900">
                <CheckCircle2 className="inline w-4 h-4 mr-1" />
                You&apos;re switching to <b>{tierLabel(pending.to_tier)}</b>
              </p>
              <p className="text-xs text-amber-800/80 mt-1">
                Takes effect at the end of your current billing cycle
                {pending.effective_at ? ` (${pending.effective_at.slice(0, 10)})` : ""}.
                Your current {tierLabel(pending.from_tier)} access stays active until then.
              </p>
            </div>
            <p className="text-xs text-slate-500">
              Changed your mind? You can undo and stay on {tierLabel(pending.from_tier)}.
            </p>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={onClose} data-testid="change-plan-close-btn">Close</Button>
              <Button onClick={undo} disabled={busy} className="rounded-lg" data-testid="undo-plan-change-btn">
                {busy ? "Undoing…" : "Undo & keep current plan"}
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-3 py-2">
            <p className="text-sm text-slate-600">
              Pick the plan you&apos;d like to switch to. The change takes effect at the end of your current billing cycle — no proration, no surprise charges.
            </p>
            {TIERS.map((t) => {
              const isCurrent = t.key === currentTier;
              const isSelected = t.key === target;
              const Icon = t.icon;
              const down = isDowngrade(currentTier, t.key);
              return (
                <button
                  key={t.key}
                  type="button"
                  disabled={isCurrent}
                  onClick={() => setTarget(t.key)}
                  className={`w-full text-left rounded-xl border-2 p-3 flex items-start gap-3 transition-all
                    ${isCurrent ? "border-slate-200 bg-slate-50 opacity-60 cursor-not-allowed" : ""}
                    ${isSelected ? "border-purple-500 bg-purple-50" : "border-slate-200 hover:border-purple-300"}`}
                  data-testid={`change-plan-tier-${t.key}`}
                >
                  <Icon className={`w-5 h-5 mt-0.5 ${isSelected ? "text-purple-600" : "text-slate-500"}`} />
                  <div className="flex-1">
                    <p className="font-semibold text-slate-900 flex items-center gap-2">
                      {t.label}
                      {isCurrent && <span className="text-[10px] uppercase font-bold text-slate-500 bg-slate-200 px-2 py-0.5 rounded-full">Current</span>}
                      {!isCurrent && down && <span className="text-[10px] uppercase font-bold text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full"><ArrowDownRight className="inline w-3 h-3" /> Downgrade</span>}
                      {!isCurrent && !down && <span className="text-[10px] uppercase font-bold text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full"><ArrowUpRight className="inline w-3 h-3" /> Upgrade</span>}
                    </p>
                    <p className="text-xs text-slate-500 mt-0.5">{t.blurb}</p>
                  </div>
                </button>
              );
            })}
            {target === "one_time" && currentTier === "royal_90" && (
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2">
                Royal pre-pays 3 months. You&apos;ll keep Royal benefits through the end of your current cycle, then drop to Monthly. No refund for the unused time.
              </p>
            )}
            <DialogFooter className="gap-2 mt-4">
              <Button variant="outline" onClick={onClose} data-testid="change-plan-cancel-btn">Cancel</Button>
              <Button
                onClick={submit}
                disabled={!target || target === currentTier || busy}
                className="bg-purple-600 hover:bg-purple-700 text-white rounded-lg"
                data-testid="change-plan-confirm-btn"
              >
                {busy ? "Saving…" : `Switch to ${tierLabel(target || "")}`}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
